/**
 * Deep-Dive-style chart derivations for ONE metric, ported from
 * `apps/frontend/features/deep-dive/utils` (the app's canonical metric graph).
 *
 * The chart draws on a **daily calendar domain** (every day from the first to the
 * last scan), with three representations aligned onto it:
 *   - Trend    = `ema`         (slow EMA — smoothed, reliable; always per scan)
 *   - Daily    = `emaFast`     (fast EMA — responsive; null on backfilled scans)
 *   - Baseline = `baselineEma` (long-run normal; null until enough history)
 * Days between scans are linearly interpolated for visual continuity; days whose
 * field is null are non-anchors and read as null (the line breaks). Routine
 * markers are placed by nearest day. No lifestyle/wearable overlay in the share.
 */
import type {
  APIShareRegionalTrendItem,
  APISharedProfileResponse,
} from '@see-stella/types'
import { sDayJs } from '@see-stella/utils'
import { seriesFor } from './model'

export interface ScanPoint {
  /** YYYY-MM-DD */
  day: string
  scanId: string
  scanAt: string
  ema: number | null
  emaFast: number | null
  baselineEma: number | null
}

export interface SeriesItem {
  value: number | null
  /** true on a real scan day (an anchor), false on an interpolated in-between day */
  isScan: boolean
  scanId: string | null
}

/** The per-scan points for a metric, oldest → newest, keyed to calendar days. */
export function scanPointsFor(
  profile: APISharedProfileResponse,
  techName: string
): ScanPoint[] {
  const series = seriesFor(profile, techName)
  if (!series) return []
  return series.data
    .map((v) => ({
      day: sDayJs(v.scanAt).format('YYYY-MM-DD'),
      scanId: v.scanId,
      scanAt: v.scanAt,
      ema: v.ema ?? null,
      emaFast: v.emaFast ?? null,
      baselineEma: v.baselineEma ?? null,
    }))
    .sort((a, b) => sDayJs(a.scanAt).valueOf() - sDayJs(b.scanAt).valueOf())
}

/**
 * Inclusive list of calendar days (YYYY-MM-DD) spanning EVERY scan across ALL
 * metrics — not just the selected one. Anchoring the axis on the full scan
 * history (like Deep Dive) keeps it stable across metric switches and, crucially,
 * guarantees every scan's day is in the domain: the cursor/knob can't fall to
 * `indexOf === -1` and snap to day 0 when the selected metric skipped a scan.
 */
export function fullDailyDomain(profile: APISharedProfileResponse): string[] {
  const days: string[] = []
  for (const series of profile.metrics) {
    for (const v of series.data) {
      if (v.scanAt) days.push(sDayJs(v.scanAt).format('YYYY-MM-DD'))
    }
  }
  days.sort()
  if (days.length === 0) return []
  let cursor = sDayJs(days[0]).startOf('day')
  const end = sDayJs(days[days.length - 1]).startOf('day')
  const out: string[] = []
  while (!cursor.isAfter(end)) {
    out.push(cursor.format('YYYY-MM-DD'))
    cursor = cursor.add(1, 'day')
  }
  return out
}

type Accessor = (p: ScanPoint) => number | null

function interpolate(
  domain: string[],
  points: ScanPoint[],
  accessor: Accessor
): SeriesItem[] {
  const byDay = new Map<string, { value: number; scanId: string }>()
  for (const p of points) {
    const v = accessor(p)
    if (v != null) byDay.set(p.day, { value: v, scanId: p.scanId })
  }
  const anchors: number[] = []
  domain.forEach((d, i) => {
    if (byDay.has(d)) anchors.push(i)
  })
  return domain.map((d, i) => {
    const exact = byDay.get(d)
    if (exact) return { value: exact.value, isScan: true, scanId: exact.scanId }
    let lo = -1
    let hi = -1
    for (const a of anchors) {
      if (a < i) lo = a
      if (a > i) {
        hi = a
        break
      }
    }
    if (lo === -1 || hi === -1)
      return { value: null, isScan: false, scanId: null }
    const loVal = byDay.get(domain[lo])!.value
    const hiVal = byDay.get(domain[hi])!.value
    const t = (i - lo) / (hi - lo)
    return { value: loVal + (hiVal - loVal) * t, isScan: false, scanId: null }
  })
}

export interface MetricChart {
  domain: string[]
  trend: SeriesItem[]
  daily: SeriesItem[]
  baseline: SeriesItem[]
  points: ScanPoint[]
  /** whether Daily / Baseline have any data (else their toggles are inert) */
  hasDaily: boolean
  hasBaseline: boolean
}

/** Everything the chart needs for one metric. */
export function buildMetricChart(
  profile: APISharedProfileResponse,
  techName: string
): MetricChart {
  const points = scanPointsFor(profile, techName)
  // Domain spans ALL scans (full history), so the selected metric's line simply
  // has null gaps where it skipped a scan, and the cursor never falls off-domain.
  const domain = fullDailyDomain(profile)
  return {
    domain,
    points,
    trend: interpolate(domain, points, (p) => p.ema),
    daily: interpolate(domain, points, (p) => p.emaFast),
    baseline: interpolate(domain, points, (p) => p.baselineEma),
    hasDaily: points.some((p) => p.emaFast != null),
    hasBaseline: points.some((p) => p.baselineEma != null),
  }
}

/**
 * A chart for ONE region (area) of the metric, over the SAME full daily domain
 * as the face-level chart — so the cursor and the scrubber stay aligned when the
 * viewer drills into a region. Built from the lazy regional-trend items (each
 * carries every area's ema, emaFast, and baselineEma for a scan), so the region
 * chart draws Trend / Daily / Baseline just like the face chart.
 */
export function buildRegionalChart(
  profile: APISharedProfileResponse,
  items: APIShareRegionalTrendItem[],
  area: string
): MetricChart {
  const points: ScanPoint[] = items
    .map((item): ScanPoint | null => {
      const r = item.regionalData.find((d) => d.area === area)
      if (!r) return null
      return {
        day: sDayJs(item.scanAt).format('YYYY-MM-DD'),
        scanId: item.scanId,
        scanAt: item.scanAt,
        ema: r.ema ?? null,
        emaFast: r.emaFast ?? null,
        baselineEma: r.baselineEma ?? null,
      }
    })
    .filter((p): p is ScanPoint => p !== null)
    .sort((a, b) => sDayJs(a.scanAt).valueOf() - sDayJs(b.scanAt).valueOf())
  const domain = fullDailyDomain(profile)
  return {
    domain,
    points,
    trend: interpolate(domain, points, (p) => p.ema),
    daily: interpolate(domain, points, (p) => p.emaFast),
    baseline: interpolate(domain, points, (p) => p.baselineEma),
    hasDaily: points.some((p) => p.emaFast != null),
    hasBaseline: points.some((p) => p.baselineEma != null),
  }
}

/** Domain index of a scan's day (for placing the cursor), or -1. */
export function dayIndexOfScan(domain: string[], scanAt: string): number {
  return domain.indexOf(sDayJs(scanAt).format('YYYY-MM-DD'))
}
